import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  Row,
  Col,
  Card,
  InputNumber,
  Switch,
  Typography,
  Upload,
  message,
  Spin,
  Tag,
  Image,
  Progress,
  Tabs,
  Descriptions,
  Divider,
  Space,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  LoadingOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { ArrowLeft, Camera, Truck, Star, Rocket, Clock } from "lucide-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import apiClient from "../../helper/apiClient";
import apiPath from "../../constants/apiPath";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

const ProductDetail = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const { pathname } = useLocation();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(pathname.includes("/edit"));
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const isUploadingRef = useRef(false);
  const messageKeyRef = useRef(null);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const response = await apiClient.get(`${apiPath.activeCategoryList}`);

      if (response.data?.success) {
        const categoryList =
          response.data.categories || response.data.data || [];
        setCategories(
          categoryList.filter(
            (cat) => cat.status === true || cat.status === "true",
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      message.error("Failed to load categories");
    }
  };

  // Fetch product data
  const fetchProductData = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await apiClient.get(
        `${apiPath.marketPlaceProductView}${id}/`,
      );

      if (response.data?.success) {
        const productData = response.data.data || response.data.product;
        setProduct(productData);

        // Set form values
        form.setFieldsValue({
          name: productData.name,
          sku: productData.sku,
          description: productData.description,
          price: parseFloat(productData.price),
          stock_quantity: productData.stock_quantity,
          category: productData.category,
          featured: productData.featured,
          bestseller: productData.bestseller,
          limited_edition: productData.limited_edition,
          status: productData.status,
          manufacturer: productData.manufacturer,
        });

        // Set uploaded images
        if (productData.images && productData.images.length > 0) {
          setUploadedImages(
            productData.images.map((img) => ({
              uid: img.id || String(img.id),
              name: img.alt_text || "image",
              status: "done",
              url: img.image_url,
              image_url: img.image_url,
              alt_text: img.alt_text,
              is_primary: img.is_primary,
            })),
          );
        }

        // Set attributes
        if (productData.attributes && productData.attributes.length > 0) {
          form.setFieldsValue({
            attributes: productData.attributes.map((attr) => ({
              name: attr.name,
              value: attr.value,
            })),
          });
        }
      } else {
        message.error(response.data?.message || "Failed to load product");
        navigate("/catalog/products");
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      message.error("Failed to load product data");
      navigate("/catalog/products");
    } finally {
      setLoading(false);
    }
  };

  // Upload multiple images
  const uploadMultipleImages = async (files) => {
    if (isUploadingRef.current) {
      console.log("Upload already in progress, ignoring...");
      return;
    }

    isUploadingRef.current = true;
    setUploadingImages(true);

    const newImages = [];
    let successCount = 0;
    let failCount = 0;

    messageKeyRef.current = message.loading(
      `Uploading ${files.length} image(s)...`,
      0,
    );

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const uploadKey = `${file.name}_${Date.now()}_${i}`;

      setUploadProgress((prev) => ({ ...prev, [uploadKey]: 0 }));

      const formData = new FormData();
      formData.append("image", file);

      try {
        const response = await apiClient.post(
          `${apiPath.imageUpload}`,
          formData,
          {
            headers: {},
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              setUploadProgress((prev) => ({
                ...prev,
                [uploadKey]: percentCompleted,
              }));
            },
          },
        );

        let imageUrl = null;
        if (response.data?.status === 200 && response.data?.data?.image_url) {
          imageUrl = response.data.data.image_url;
        } else if (response.data?.success && response.data?.image_url) {
          imageUrl = response.data.image_url;
        } else if (response.data?.url) {
          imageUrl = response.data.url;
        } else if (response.data?.image_url) {
          imageUrl = response.data.image_url;
        }

        if (imageUrl) {
          const isPrimary =
            uploadedImages.length === 0 && newImages.length === 0;
          const imageData = {
            uid: `${Date.now()}_${i}_${Math.random()}`,
            name: file.name,
            status: "done",
            url: imageUrl,
            image_url: imageUrl,
            alt_text: file.name,
            is_primary: isPrimary,
          };
          newImages.push(imageData);
          successCount++;
        } else {
          failCount++;
          console.error(`Failed to upload ${file.name}:`, response.data);
        }
      } catch (error) {
        failCount++;
        console.error(`Error uploading ${file.name}:`, error);

        if (error.response) {
          if (error.response.status === 401) {
            message.error("Authentication failed. Please login again.");
          } else {
            message.error(
              `Upload failed: ${error.response.data?.detail || error.response.data?.message || "Unknown error"}`,
            );
          }
        }
      } finally {
        setUploadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[uploadKey];
          return newProgress;
        });
      }
    }

    if (messageKeyRef.current) {
      messageKeyRef.current();
    }

    if (successCount > 0 && failCount === 0) {
      message.success(`✓ ${successCount} image(s) uploaded successfully`);
    } else if (successCount > 0 && failCount > 0) {
      message.warning(`Uploaded ${successCount} image(s), ${failCount} failed`);
    } else if (failCount > 0) {
      message.error(`Failed to upload ${failCount} image(s)`);
    }

    setUploadedImages((prevImages) => [...prevImages, ...newImages]);
    setUploadingImages(false);
    isUploadingRef.current = false;
  };

  // Handle file selection
  const handleFileChange = (info) => {
    const { fileList } = info;

    if (fileList && fileList.length > 0) {
      const filesToUpload = fileList
        .filter((f) => f.originFileObj)
        .map((f) => f.originFileObj);

      if (filesToUpload.length > 0 && !isUploadingRef.current && isEditMode) {
        uploadMultipleImages(filesToUpload);
      }
    }
  };

  // Handle image removal
  const handleImageRemove = (file) => {
    setUploadedImages(uploadedImages.filter((img) => img.uid !== file.uid));
    message.success("Image removed");
  };

  // Handle setting primary image
  const setPrimaryImage = (uid) => {
    setUploadedImages(
      uploadedImages.map((img) => ({
        ...img,
        is_primary: img.uid === uid,
      })),
    );
    message.success("Primary image set");
  };

  // Handle form submission
  const onFinish = async (values) => {
    if (uploadedImages.length === 0) {
      message.warning("Please upload at least one product image");
      return;
    }

    setSubmitting(true);

    const productData = {
      name: values.name,
      description: values.description,
      price: parseFloat(values.price),
      sku: values.sku,
      category: values.category,
      manufacturer: values.manufacturer || "",
      stock_quantity: values.stock_quantity || 1,
      featured: values.featured || false,
      status: values.status !== undefined ? values.status : true,
      bestseller: values.bestseller || false,
      limited_edition: values.limited_edition || false,
      specifications: values.specifications || {},
      images: uploadedImages.map((img) => ({
        image_url: img.image_url || img.url,
        alt_text: img.alt_text || img.name,
        is_primary: img.is_primary || false,
      })),
      attributes: values.attributes || [],
    };

    try {
      const response = await apiClient.put(
        `${apiPath.marketPlaceProductUpdate}${id}/`,
        productData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data?.success) {
        message.success("Product updated successfully");
        setIsEditMode(false);
        fetchProductData(); // Refresh data
      } else {
        message.error(response.data?.message || "Failed to update product");
      }
    } catch (error) {
      console.error("Error updating product:", error);
      message.error(
        error.response?.data?.message || "Failed to update product",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Custom request handler
  const customRequest = ({ onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 100);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditMode(false);
    fetchProductData(); // Reset form to original data
  };

  useEffect(() => {
    fetchCategories();
    fetchProductData();
    setIsEditMode(pathname.includes("/edit"));
  }, [id, pathname]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  const primaryImage = uploadedImages.find((img) => img.is_primary);
  const getStockStatus = () => {
    if (product?.stock_quantity === 0)
      return { text: "Out of Stock", color: "red" };
    if (product?.stock_quantity <= 10)
      return { text: "Low Stock", color: "orange" };
    return { text: "In Stock", color: "green" };
  };

  return (
    <div style={{ padding: 24, background: "#f0f2f5", minHeight: "100vh" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          background: "#fff",
          padding: "16px 24px",
          borderRadius: 8,
        }}
      >
        <div
          className="left"
          style={{ display: "flex", gap: 10, alignItems: "center" }}
        >
          <Button
            icon={<ArrowLeft size={16} />}
            onClick={() => navigate("/catalog/products")}
          />
          <Title level={4} style={{ margin: 0 }}>
            {isEditMode ? "Edit Product" : "Product Details"}
          </Title>
          {!isEditMode && product && (
            <Tag
              color={product.status ? "green" : "red"}
              style={{ marginLeft: 10 }}
            >
              {product.status ? "Active" : "Inactive"}
            </Tag>
          )}
        </div>

        <div className="right" style={{ display: "flex", gap: 10 }}>
          {!isEditMode ? (
            <>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setIsEditMode(true)}
              >
                Edit Product
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCancelEdit} icon={<CloseOutlined />}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => form.submit()}
                loading={submitting}
                icon={<SaveOutlined />}
              >
                Save Changes
              </Button>
            </>
          )}
        </div>
      </div>

      {!isEditMode && product ? (
        // View Mode
        <>
          <Row gutter={20}>
            <Col xs={24} lg={16}>
              {/* Product Images */}
              <Card title="Product Images" style={{ marginBottom: 20 }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <div
                      style={{
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                        padding: 16,
                        textAlign: "center",
                      }}
                    >
                      <Image
                        src={
                          primaryImage?.url ||
                          uploadedImages[0]?.url ||
                          "/placeholder.png"
                        }
                        alt={product.name}
                        style={{
                          width: "100%",
                          maxHeight: 300,
                          objectFit: "contain",
                        }}
                        preview={true}
                      />
                    </div>
                  </Col>
                  <Col span={16}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                      {uploadedImages.map((img, idx) => (
                        <div
                          key={img.uid || idx}
                          style={{ textAlign: "center" }}
                        >
                          <Image
                            src={img.url}
                            alt={img.alt_text}
                            width={80}
                            height={80}
                            style={{
                              objectFit: "cover",
                              borderRadius: 4,
                              cursor: "pointer",
                            }}
                            preview={true}
                          />
                          {img.is_primary && (
                            <Tag
                              color="green"
                              style={{ marginTop: 4, display: "block" }}
                            >
                              Primary
                            </Tag>
                          )}
                        </div>
                      ))}
                    </div>
                  </Col>
                </Row>
              </Card>

              {/* Product Information */}
              <Card title="Product Information" style={{ marginBottom: 20 }}>
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Product Name" span={2}>
                    <Text strong>{product.name}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="SKU">
                    {product.sku || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Brand">
                    {product.manufacturer || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Category">
                    {product.category_name || "Uncategorized"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Points">
                    <Text
                      style={{
                        color: "#ff4d4f",
                        fontSize: 18,
                        fontWeight: 600,
                      }}
                    >
                      ${parseFloat(product.price).toFixed(2)}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Stock Quantity" span={2}>
                    <Space>
                      <Text>{product.stock_quantity}</Text>
                      <Tag color={getStockStatus().color}>
                        {getStockStatus().text}
                      </Tag>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Description" span={2}>
                    {product.description || "No description available"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {/* Product Attributes */}
              {product.attributes && product.attributes.length > 0 && (
                <Card title="Product Attributes" style={{ marginBottom: 20 }}>
                  <Row gutter={[16, 16]}>
                    {product.attributes.map((attr, idx) => (
                      <Col span={12} key={idx}>
                        <div
                          style={{
                            background: "#f5f5f5",
                            padding: "12px",
                            borderRadius: 8,
                          }}
                        >
                          <Text type="secondary">{attr.name}</Text>
                          <div>
                            <Text strong>{attr.value}</Text>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card>
              )}
            </Col>

            <Col xs={24} lg={8}>
              {/* Product Status */}
              <Card title="Product Status" style={{ marginBottom: 20 }}>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">Product ID</Text>
                  <div>
                    <Text strong>#{product.id}</Text>
                  </div>
                </div>
                <Divider />
                <Space
                  direction="vertical"
                  size="middle"
                  style={{ width: "100%" }}
                >
                  {product.featured && (
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <Star size={16} color="#faad14" />
                      <Text>Featured Product</Text>
                    </div>
                  )}
                  {product.bestseller && (
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <Rocket size={16} color="#13c2c2" />
                      <Text>Best Seller</Text>
                    </div>
                  )}
                  {product.limited_edition && (
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <Clock size={16} color="#722ed1" />
                      <Text>Limited Edition</Text>
                    </div>
                  )}
                </Space>
              </Card>
            </Col>
          </Row>
        </>
      ) : (
        // Edit Mode - Form
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={20}>
            {/* LEFT COLUMN */}
            <Col xs={24} lg={18}>
              {/* General Information */}
              <Card title="General Information" className="card">
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="sku"
                      label="SKU"
                      rules={[{ required: true, message: "Please enter SKU" }]}
                    >
                      <Input placeholder="Enter product SKU" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="name"
                      label="Product Name"
                      rules={[
                        {
                          required: true,
                          message: "Please enter product name",
                        },
                      ]}
                    >
                      <Input placeholder="Enter product name" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="category"
                      label="Category"
                      rules={[
                        { required: true, message: "Please select category" },
                      ]}
                    >
                      <Select placeholder="Select category" showSearch>
                        {categories.map((cat) => (
                          <Option key={cat.id} value={cat.id}>
                            {cat.title || cat.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="manufacturer" label="Brand">
                      <Input placeholder="Enter brand name" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="description" label="Description">
                  <TextArea rows={4} placeholder="Enter product description" />
                </Form.Item>
              </Card>

              {/* Product Attributes */}
              <Card title="Product Attributes" className="card">
                <Form.List name="attributes">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field, index) => (
                        <div key={field.key} style={{ marginBottom: 16 }}>
                          <Row gutter={16} align="middle">
                            <Col span={10}>
                              <Form.Item
                                {...field}
                                name={[field.name, "name"]}
                                fieldKey={[field.fieldKey, "name"]}
                                label={index === 0 ? "Attribute Name" : ""}
                                rules={[
                                  {
                                    required: true,
                                    message: "Enter attribute name",
                                  },
                                ]}
                              >
                                <Select placeholder="Select attribute">
                                  <Option value="Color">Color</Option>
                                  <Option value="Size">Size</Option>
                                  <Option value="Material">Material</Option>
                                  <Option value="Weight">Weight</Option>
                                  <Option value="Dimensions">Dimensions</Option>
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col span={10}>
                              <Form.Item
                                {...field}
                                name={[field.name, "value"]}
                                fieldKey={[field.fieldKey, "value"]}
                                label={index === 0 ? "Value" : ""}
                                rules={[
                                  { required: true, message: "Enter value" },
                                ]}
                              >
                                <Input placeholder="e.g., Red, Large, Cotton" />
                              </Form.Item>
                            </Col>
                            <Col span={4} style={{ textAlign: "center" }}>
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => remove(field.name)}
                                style={{ marginTop: index === 0 ? 24 : 0 }}
                              />
                            </Col>
                          </Row>
                        </div>
                      ))}
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        block
                        icon={<PlusOutlined />}
                      >
                        Add Attribute
                      </Button>
                    </>
                  )}
                </Form.List>
              </Card>

              {/* Product Images */}
              <Card title="Product Images" className="card">
                <div className="image-gallery">
                  {uploadedImages.map((img) => (
                    <div key={img.uid} className="image-item">
                      <Image
                        src={img.url}
                        alt={img.alt_text}
                        width={120}
                        height={120}
                        style={{ objectFit: "cover" }}
                        preview={false}
                      />
                      <div className="image-actions">
                        {!img.is_primary && (
                          <Button
                            size="small"
                            type="primary"
                            onClick={() => setPrimaryImage(img.uid)}
                          >
                            Set Primary
                          </Button>
                        )}
                        {img.is_primary && <Tag color="green">Primary</Tag>}
                        <Button
                          size="small"
                          danger
                          onClick={() => handleImageRemove(img)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}

                  <Upload
                    customRequest={customRequest}
                    onChange={handleFileChange}
                    showUploadList={false}
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    multiple={true}
                    disabled={uploadingImages}
                  >
                    <div className="upload-content-box">
                      {uploadingImages ? <LoadingOutlined spin /> : <Camera />}
                      <div className="upload-inner" style={{ marginTop: 8 }}>
                        {uploadingImages ? "Uploading..." : "Add Images"}
                      </div>
                      <div>png, jpeg, jpg</div>
                    </div>
                  </Upload>
                </div>

                {uploadingImages && Object.keys(uploadProgress).length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <Progress
                      percent={Math.floor(
                        (Object.keys(uploadProgress).filter(
                          (key) => uploadProgress[key] === 100,
                        ).length /
                          Object.keys(uploadProgress).length) *
                          100,
                      )}
                      status="active"
                    />
                    <small>
                      Uploading {Object.keys(uploadProgress).length} image(s)...
                    </small>
                  </div>
                )}
              </Card>
            </Col>

            {/* RIGHT COLUMN */}
            <Col xs={24} lg={6} className="sticky">
              {/* Points/Price */}
              <Card title="Points & Pricing" className="card">
                <Form.Item
                  name="price"
                  label="Points"
                  rules={[
                    { required: true, message: "Please enter points/price" },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    placeholder="Enter points"
                    formatter={(value) =>
                      ` ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) => value.replace(/₹\s?|(,*)/g, "")}
                  />
                </Form.Item>
              </Card>

              {/* Settings */}
              <Card title="Settings" className="card">
                <div className="switch-row">
                  <Text>Featured Product</Text>
                  <Form.Item name="featured" valuePropName="checked" noStyle>
                    <Switch />
                  </Form.Item>
                </div>

                <div className="switch-row">
                  <Text>Limited Edition</Text>
                  <Form.Item
                    name="limited_edition"
                    valuePropName="checked"
                    noStyle
                  >
                    <Switch />
                  </Form.Item>
                </div>

                <div className="switch-row">
                  <Text>Bestseller</Text>
                  <Form.Item name="bestseller" valuePropName="checked" noStyle>
                    <Switch />
                  </Form.Item>
                </div>

                <div className="switch-row">
                  <Text>Active Status</Text>
                  <Form.Item name="status" valuePropName="checked" noStyle>
                    <Switch defaultChecked />
                  </Form.Item>
                </div>
              </Card>

              {/* Inventory */}
              <Card title="Inventory" className="card">
                <Form.Item
                  name="stock_quantity"
                  label="Stock Quantity"
                  rules={[
                    { required: true, message: "Please enter stock quantity" },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    placeholder="Enter stock quantity"
                  />
                </Form.Item>
              </Card>
            </Col>
          </Row>
        </Form>
      )}

      <style jsx>{`
        .card {
          margin-bottom: 16px;
          border-radius: 8px;
        }

        .sticky {
          position: sticky;
          top: 20px;
        }

        .switch-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .image-gallery {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
        }

        .image-item {
          position: relative;
          width: 120px;
          border: 1px solid #d9d9d9;
          border-radius: 8px;
          overflow: hidden;
        }

        .image-actions {
          padding: 8px;
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .upload-content-box {
          width: 120px;
          height: 120px;
          border: 1px dashed #d9d9d9;
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          background: #fafafa;
        }

        .upload-content-box:hover {
          border-color: #1890ff;
        }

        .upload-inner {
          font-size: 12px;
          color: #666;
        }
      `}</style>
    </div>
  );
};

export default ProductDetail;
